import React, { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useParams } from 'react-router-dom';
import { devOpsApiRef } from '../../apis';
import ReactMarkdown from 'react-markdown';
import { makeStyles, Typography, Paper, CircularProgress } from '@material-ui/core';
import { Content, Header, Page } from '@backstage/core-components';
import { sanitizeMarkdownLinkUrls } from '../../utils/markdownStyles';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 900,
    margin: '0 auto',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  errorContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  markdownContent: {
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: theme.palette.text.primary,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      fontWeight: 600,
    },
    '& h1': {
      fontSize: '1.5rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: theme.spacing(0.5),
    },
    '& h2': {
      fontSize: '1.25rem',
    },
    '& h3': {
      fontSize: '1.1rem',
    },
    '& p': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      '&:hover': {
        color: theme.palette.primary.dark,
        textDecoration: 'underline',
      },
      '&:visited': {
        color: theme.palette.primary.dark,
      },
    },
    '& ul, & ol': {
      marginLeft: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      padding: '2px 4px',
      borderRadius: '3px',
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      padding: theme.spacing(1),
      borderRadius: '4px',
      overflow: 'auto',
      marginBottom: theme.spacing(1),
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      marginLeft: 0,
      paddingLeft: theme.spacing(2),
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 600,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(2, 0),
    },
  },
}));

// Helper functions to extract display information
const getComponentTitle = (entity: Entity | null, componentName: string): string => {
  return entity?.metadata?.title || componentName;
};

const getLifecycleName = (entity: Entity | null): string => {
  const lifecycle = entity?.spec?.lifecycle as string;
  if (!lifecycle) return 'Unknown';
  
  switch (lifecycle.toLowerCase()) {
    case 'test':
    case 't':
      return 'Test';
    case 'acceptance':
    case 'a':
      return 'Acceptance';
    case 'production':
    case 'p':
      return 'Production';
    default:
      return lifecycle.charAt(0).toUpperCase() + lifecycle.slice(1);
  }
};

export const ChangeLogPage = () => {
  const classes = useStyles();
  const { componentEntity, fileName } = useParams<{ componentEntity: string; fileName: string }>();
  const devOpsApi = useApi(devOpsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entity, setEntity] = useState<Entity | null>(null);

  useEffect(() => {
    if (!componentEntity || !fileName) {
      setError('Missing component or file name in route');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    
    // Fetch both the entity information and the markdown content
    Promise.all([
      catalogApi.getEntityByRef(`component:default/${componentEntity}`),
      devOpsApi.getChangeLogMarkdown(componentEntity, fileName)
    ])
      .then(([entityData, markdownData]) => {
        setEntity(entityData || null);
        setMarkdown(markdownData);
      })
      .catch(e => setError(e.message || 'Failed to load change log'))
      .finally(() => setLoading(false));
  }, [componentEntity, fileName, devOpsApi, catalogApi]);

  const componentTitle = getComponentTitle(entity, componentEntity || 'Unknown');
  const lifecycle = getLifecycleName(entity);

  return (
    <Page themeId="tool">
      <Header 
        title={`Change Log: ${componentTitle}`}
        subtitle={`${lifecycle} Environment`}
      />
      <Content>
        <div className={classes.container}>
          {loading && (
            <Paper className={classes.paper}>
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} />
                <Typography variant="body2">Loading change log...</Typography>
              </div>
            </Paper>
          )}
          
          {error && (
            <div className={classes.errorContainer}>
              <Typography variant="body2">
                <strong>Error:</strong> {error}
              </Typography>
            </div>
          )}
          
          {!loading && !error && markdown && (
            <Paper className={classes.paper}>
              <Typography variant="h5" gutterBottom style={{ marginBottom: '16px' }}>
                {componentTitle} ({lifecycle})
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom style={{ marginBottom: '24px' }}>
                {fileName}
              </Typography>
              <div className={classes.markdownContent}>
                <ReactMarkdown>{sanitizeMarkdownLinkUrls(markdown)}</ReactMarkdown>
              </div>
            </Paper>
          )}
        </div>
      </Content>
    </Page>
  );
};

export default ChangeLogPage; 